"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import {
  CONSENT_UPDATED_EVENT,
  writeTrackingConsentSnapshot,
} from "@/lib/consent/client";
import { DEFAULT_CONSENT_PREFERENCES } from "@/lib/consent/defaults";
import type { ConsentPreferences, ConsentResponse } from "@/lib/consent/types";

function formatUpdatedAt(value: string | null) {
  if (!value) return "Not set";
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return "Not set";
  return new Intl.DateTimeFormat("en-ZA", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(date);
}

function ToggleRow({
  label,
  description,
  checked,
  disabled,
  onChange,
}: {
  label: string;
  description: string;
  checked: boolean;
  disabled?: boolean;
  onChange: (next: boolean) => void;
}) {
  return (
    <label className="flex items-start justify-between gap-4 rounded-2xl border border-white/10 bg-white/5 px-4 py-3">
      <span className="min-w-0">
        <span className="block text-sm font-medium text-[var(--text)]">{label}</span>
        <span className="mt-1 block text-xs text-[var(--muted)]">{description}</span>
      </span>
      <input
        type="checkbox"
        checked={checked}
        disabled={Boolean(disabled)}
        onChange={(event) => onChange(event.target.checked)}
        className="mt-1 h-4 w-4 rounded border-white/20 bg-white/10 text-[var(--accent)] focus:ring-[var(--accent)] disabled:cursor-not-allowed disabled:opacity-60"
      />
    </label>
  );
}

export default function ConsentPreferencesPanel() {
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [status, setStatus] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [preferences, setPreferences] = useState<ConsentPreferences>({
    ...DEFAULT_CONSENT_PREFERENCES,
  });
  const [savedPreferences, setSavedPreferences] = useState<ConsentPreferences>({
    ...DEFAULT_CONSENT_PREFERENCES,
  });
  const [updatedAt, setUpdatedAt] = useState<string | null>(null);
  const statusTimeoutRef = useRef<number | null>(null);

  const clearStatusTimeout = () => {
    if (statusTimeoutRef.current === null) return;
    window.clearTimeout(statusTimeoutRef.current);
    statusTimeoutRef.current = null;
  };

  useEffect(() => {
    let active = true;

    const load = async () => {
      setIsLoading(true);
      setErrorMessage(null);

      try {
        const response = await fetch("/api/account/consents", {
          cache: "no-store",
        });
        if (!response.ok) {
          const payload = await response.json().catch(() => null);
          throw new Error(payload?.error || "consent_fetch_failed");
        }

        const payload = (await response.json()) as ConsentResponse;
        if (!active) return;
        const next = payload.preferences;
        setPreferences(next);
        setSavedPreferences(next);
        setUpdatedAt(payload.updated_at);
        writeTrackingConsentSnapshot(next);
      } catch (error) {
        if (!active) return;
        const message = error instanceof Error ? error.message : "consent_fetch_failed";
        setErrorMessage(`Could not load consent settings (${message}).`);
      } finally {
        if (active) setIsLoading(false);
      }
    };

    void load();
    return () => {
      active = false;
    };
  }, []);

  useEffect(
    () => () => {
      clearStatusTimeout();
    },
    []
  );

  const isDirty = useMemo(
    () =>
      preferences.analytics_tracking !== savedPreferences.analytics_tracking ||
      preferences.session_replay !== savedPreferences.session_replay ||
      preferences.product_updates_email !== savedPreferences.product_updates_email ||
      preferences.marketing_email !== savedPreferences.marketing_email,
    [preferences, savedPreferences]
  );

  const handleSave = async () => {
    if (isSaving || !isDirty) return;
    setIsSaving(true);
    clearStatusTimeout();
    setStatus(null);
    setErrorMessage(null);

    try {
      const response = await fetch("/api/account/consents", {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...preferences,
          source: "account_settings",
        }),
      });

      if (!response.ok) {
        const payload = await response.json().catch(() => null);
        throw new Error(payload?.error || "consent_update_failed");
      }

      const payload = (await response.json()) as ConsentResponse;
      setPreferences(payload.preferences);
      setSavedPreferences(payload.preferences);
      setUpdatedAt(payload.updated_at);
      setStatus("Saved");

      writeTrackingConsentSnapshot(payload.preferences);
      window.dispatchEvent(
        new CustomEvent(CONSENT_UPDATED_EVENT, {
          detail: payload.preferences,
        })
      );
      statusTimeoutRef.current = window.setTimeout(() => {
        setStatus(null);
        statusTimeoutRef.current = null;
      }, 1800);
    } catch (error) {
      const message = error instanceof Error ? error.message : "consent_update_failed";
      setErrorMessage(`Could not save consent settings (${message}).`);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <section className="panel space-y-5 p-6">
      <div>
        <p className="font-[var(--font-display)] text-sm uppercase tracking-[0.22em] text-[var(--text)]">
          Consent
        </p>
        <p className="mt-2 text-sm text-[var(--muted)]">
          Manage tracking and email preferences for deepface.dev.
        </p>
      </div>

      {errorMessage ? (
        <div className="rounded-2xl border border-amber-400/40 bg-amber-500/10 px-4 py-3 text-xs text-amber-100">
          {errorMessage}
        </div>
      ) : null}

      <div className="space-y-3">
        <p className="text-2xs uppercase tracking-[0.3em] text-[var(--muted)]">
          Tracking
        </p>
        <ToggleRow
          label="Analytics tracking"
          description="Allow anonymized product analytics and page telemetry."
          checked={preferences.analytics_tracking}
          disabled={isLoading || isSaving}
          onChange={(next) =>
            setPreferences((prev) => ({
              ...prev,
              analytics_tracking: next,
              session_replay: next ? prev.session_replay : false,
            }))
          }
        />
        <ToggleRow
          label="Session replay"
          description="Allow session replay capture for support and debugging."
          checked={preferences.session_replay}
          disabled={isLoading || isSaving || !preferences.analytics_tracking}
          onChange={(next) =>
            setPreferences((prev) => ({
              ...prev,
              analytics_tracking: next ? true : prev.analytics_tracking,
              session_replay: next,
            }))
          }
        />
      </div>

      <div className="space-y-3">
        <p className="text-2xs uppercase tracking-[0.3em] text-[var(--muted)]">
          Emails
        </p>
        <ToggleRow
          label="Product updates"
          description="Receive release notes, operational updates, and service announcements."
          checked={preferences.product_updates_email}
          disabled={isLoading || isSaving}
          onChange={(next) =>
            setPreferences((prev) => ({ ...prev, product_updates_email: next }))
          }
        />
        <ToggleRow
          label="Marketing emails"
          description="Receive news, campaigns, and promotional content."
          checked={preferences.marketing_email}
          disabled={isLoading || isSaving}
          onChange={(next) =>
            setPreferences((prev) => ({ ...prev, marketing_email: next }))
          }
        />
      </div>

      <div className="flex items-center justify-between">
        <button
          type="button"
          onClick={handleSave}
          disabled={isLoading || isSaving || !isDirty}
          className="rounded-full border border-white/20 bg-white/5 px-5 py-2 text-xs font-semibold uppercase tracking-[0.3em] text-[var(--text)] transition hover:border-white/40 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {isSaving ? "Saving..." : "Save"}
        </button>
        <div className="text-right">
          {status ? (
            <p className="text-xs uppercase tracking-[0.3em] text-[var(--muted)]">
              {status}
            </p>
          ) : null}
          <p className="mt-1 text-2xs uppercase tracking-[0.2em] text-[var(--muted)]">
            Updated: {formatUpdatedAt(updatedAt)}
          </p>
        </div>
      </div>
    </section>
  );
}
